<template>
  <div
    ref="searchField"
    class="relative"
    :data-test-id="dataTestId"
    :class="'search-field ' + this.wrapperClassName"
  >
    <BaseInputField
      aria-label="Event type"
      v-bind="$props"
      :model-value="search"
      :placeholder="placeholder"
      :errors="errorMessagesInTooltip ? [] : allErrors"
      :is-marked-as-invalid="allErrors.length > 0"
      autocomplete="off"
      tabindex="-1"
      @input="input"
      @click="$emit('click', $event)"
      @focus="focus"
      @blur="blur"
      @keydown.up="changePosition(-1)"
      @keydown.down="changePosition(1)"
      @keydown.enter="enter"
      @keydown.space="selectOption()"
    >
      <template #suffixIcon v-if="chevronIcon">
        <ChevronDownIcon class="w-4 h-4 mr-1 text-grey-500" />
      </template>
    </BaseInputField>

    <transition name="fade">
      <div
        v-if="isOpen && (search.length > 0 || openOnFocus)"
        class="search-field__options shadow-elevation-large rounded-md"
        ref="options"
      >
        <div v-for="(option, index) in filteredOptions" v-if="filteredOptions.length" :key="option.value">
          <div
            class="search-field__options__option"
            :class="{
              'search-field__options__option--selected': position === index,
              'search-field__options__option--large': optionSize === 'large'
            }"
            :data-index="index"
            tabindex="-1"
            @click="selectOption(option.value)"
            v-html="option.label"
          />
        </div>

        <div v-else>
          <div class="px-4 py-3 text-grey-600 text-sm">
            {{ validationMessages.emptyList }}
          </div>
        </div>
      </div>
    </transition>
    <div v-if="errorMessagesInTooltip && allErrors.length" class="absolute left-1/2 bottom-0 w-0 h-0">
      <Tooltip :text="allErrors[0]" theme="warning" position="bottom" trigger="init" />
    </div>
  </div>
</template>

<script>
import ChevronDownIcon from '@tagvenue/icons/24/bold/arrows/chevron-down.svg';
import sortBy from 'lodash/sortBy';

import htmlProps from '../htmlProps.js';
import Tooltip from '../Tooltip.vue';
import BaseInputField from './BaseInputField.vue';
import commonProps from './common-props-mixin';

function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function boldKeywords(label, search) {
  return label.replace(new RegExp(`(${escapeRegExp(search)})`, 'gi'), '<strong>$1</strong>');
}

function boldKeywordsEnhanced(label, search) {
  const labelParts = label.split(/\s+/gi);
  search.split(/\s+/gi).forEach((word) => {
    const trimmed = word.trim();
    const containsForbiddenChars = /[/<>\\()[\]*+?|^$.{}]/i.test(trimmed);
    if (!trimmed || containsForbiddenChars) {
      return;
    }
    const regExp = new RegExp(`(${trimmed})`, 'gi');
    for (let i = 0; i < labelParts.length; i += 1) {
      const part = labelParts[i];
      if (part.match(regExp) && !part.includes('<strong>')) {
        labelParts[i] = part.replace(regExp, '<strong>$1</strong>');
        break;
      }
    }
  });

  return labelParts.join(' ');
}

export default {
  name: 'SearchField',
  emits: ['click', 'update:modelValue', 'change', 'focus', 'blur', 'openChanged'],
  mixins: [commonProps, htmlProps],
  components: {
    BaseInputField,
    Tooltip,
    ChevronDownIcon,
  },
  props: {
    id: {
      type: String,
      default: null,
    },
    modelValue: {
      type: [String, Number],
      default: null,
    },
    placeholder: {
      type: String,
      required: true,
    },
    useEnhancedSearch: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array, // objects with { label, value }
      required: true,
      validation: (arr) => arr.length > 0,
    },
    errors: {
      type: Array,
      default: () => [],
    },
    removeValueOnType: {
      type: Boolean,
      default: false,
    },
    optionSize: {
      type: String,
      validation: (size) => ['large', '', 'normal'].includes(size),
      default: '',
    },
    errorMessagesInTooltip: {
      type: Boolean,
      default: false,
    },
    skipInternalValidation: {
      type: Boolean,
      default: false,
    },
    // Field may be left empty. Unresolved text is still reported, so a
    // half-typed value cannot silently wipe the current selection.
    isOptional: {
      type: Boolean,
      default: false,
    },
    validationMessages: {
      type: Object,
      default: () => {
        return {
          emptyValue: 'Please select an event type',
          chooseFromList: 'Please select an event type from the list',
          emptyList: 'We don\'t recognise that event type, try describing it differently',
        };
      },
    },
    light: {
      type: Boolean,
      default: false,
    },
    openOnFocus: {
      type: Boolean,
      default: false,
    },
    chevronIcon: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    const option = this.options.find(opt => opt.value === this.modelValue) || {};
    return {
      val: this.modelValue,
      search: option.label || '',
      isOpen: false,
      position: -1,
      errorsData: [],
    };
  },

  computed: {
    allErrors() {
      return this.skipInternalValidation ? this.errors : [...this.errors, ...this.errorsData];
    },

    wrapperClassName() {
      return this.allErrors.length ? 'search-field--errors' : '';
    },

    filteredOptions() {
      if (!this.search.trim()) {
        return this.options;
      }

      if (this.useEnhancedSearch) {
        const maxLength = Math.max(...this.options.map((option) => option.label.length));
        return this.options
          .filter((option) => {
            option.score = this.calculateSearchScoreForOption(option);

            const labelLower = option.label.toLowerCase();
            const searchLower = this.search.trim().toLowerCase();

            option.secondScore = maxLength;
            if (labelLower.startsWith(searchLower)) {
              option.secondScore = Math.abs(labelLower.length - searchLower.length);
            }

            return option.score > 0;
          })
          .sort((a, b) => {
            if (a.score > b.score) {
              return -1;
            }
            if (a.score < b.score) {
              return 1;
            }
            if (a.secondScore === b.secondScore) {
              return a.label.length - b.label.length;
            }
            return a.secondScore - b.secondScore;
          })
          .slice(0, 20)
          .map(({ label, value, score, secondScore }) => {
            return {
              label: boldKeywordsEnhanced(label, this.search.trim()),
              rawLabel: label,
              value,
              score,
              secondScore,
            };
          });
      }

      const filteredOptions = [...this.options]
        .filter(option => option.label.toLowerCase().includes(this.search.trim().toLowerCase()));

      let options = sortBy(filteredOptions, option => {
        const startsWithSearch = option.label.toLowerCase().startsWith(this.search.trim().toLowerCase());
        // We want options that start with search to be at the top. This hack works because options are sorted lexicographically.
        const prefix = startsWithSearch ? '0000000' : 'ZZZZZZZ';
        return `${prefix}${option.label}`;
      });

      options = options.map(({ label, value }) => {
        return {
          label: boldKeywords(label, this.search.trim()),
          rawLabel: label,
          value,
        };
      });

      return options;
    },
  },

  watch: {
    modelValue(value) {
      this.val = value;
    },
    val(val) {
      this.position = -1;
      this.$emit('update:modelValue', val);
      this.updateSearch(val);
    },
    isOpen(isOpen) {
      this.$emit('openChanged', isOpen);

      if (isOpen) {
        document.addEventListener('click', this.handleOutsideEvent);
        document.addEventListener('keydown', this.handleOutsideEvent);
        return;
      }
      document.removeEventListener('click', this.handleOutsideEvent);
      document.removeEventListener('keydown', this.handleOutsideEvent);
    },
    options() {
      this.updateSearch(this.modelValue);
    },
    filteredOptions(filteredOptions) {
      if (this.filteredOptions.length !== 1) {
        return;
      }
      const [{ value, label }] = filteredOptions;
      if (label === this.search) {
        this.val = value;
      }
    },
  },

  methods: {
    input(val) {
      if (this.removeValueOnType) {
        this.val = null;
        this.$emit('update:modelValue', null);
      }
      this.search = val;
      this.isOpen = true;
      this.errorsData = [];
    },
    updateSearch(val) {
      if (this.removeValueOnType && val === null) {
        return;
      }
      const option = this.options.find(opt => opt.value === val) || {};
      this.search = option.label || '';
    },
    scrollIntoViewIfNeeded() {
      const options = this.$refs.options;
      if (!options) {
        return;
      }

      const optionElement = this.$refs.options.querySelector(`.search-field__options__option[data-index="${this.position}"]`);
      const elementHeight = optionElement.getBoundingClientRect().height;
      const elementOffsetTop = optionElement.offsetTop;
      const elementEnd = elementOffsetTop + elementHeight;

      const optionsHeight = options.getBoundingClientRect().height;
      const optionsScroll = options.scrollTop;

      if (optionsScroll + optionsHeight < elementEnd) {
        options.scrollTop = elementEnd - optionsHeight;
      }

      if (optionsScroll > elementOffsetTop) {
        options.scrollTop = elementOffsetTop;
      }
    },
    changePosition(change) {
      const optionsLength = this.filteredOptions.length;
      if (optionsLength === 0) {
        return;
      }
      if (this.position === -1 && change === -1) {
        this.position = optionsLength - 1;
      } else {
        this.position += change;
      }
      this.position = (this.position + optionsLength) % optionsLength;
      this.scrollIntoViewIfNeeded();
    },
    enter() {
      if (!this.filteredOptions.length) {
        return;
      }
      if (this.position === -1) {
        this.selectOption(this.filteredOptions[0].value);
      } else {
        this.selectOption();
      }
    },
    selectOption(value, updated = true) {
      if (this.position === -1 && !value) {
        return;
      }
      if (this.val === value) {
        this.updateSearch(value);
      } else if (typeof value !== 'string' && this.position !== -1) {
        this.val = this.filteredOptions[this.position].value;
      } else {
        this.val = value;
      }

      this.isOpen = false;
      this.errorsData = [];

      if (updated) {
        this.$emit('update:modelValue', this.val);
        this.$emit('change', this.val);
      }
    },
    handleOutsideEvent(event) {
      if (this.$refs.searchField && !this.$refs.searchField.contains(event.target)) {
        this.isOpen = false;
      }

      this.$nextTick(() => {
        const activeElement = document.activeElement;
        if (this.$refs.searchField && !this.$refs.searchField.contains(activeElement)) {
          this.isOpen = false;
        }
      });
    },
    validate() {
      const option = this.options.find(opt => opt.value === this.modelValue) || {};
      if (this.search !== option.label) {
        const searchValue = this.search.trim().toLowerCase();
        let foundSimilar = false;
        this.filteredOptions.find(({ value, rawLabel }) => {
          if (value.toString().includes(searchValue) || rawLabel.toLowerCase().includes(searchValue)) {
            foundSimilar = true;
            return true;
          }
          return false;
        });
        this.errorsData = foundSimilar ? [this.validationMessages.chooseFromList] : [this.validationMessages.emptyList];
        return { isValid: false };
      }
      return { isValid: true };
    },
    focus(event) {
      this.isOpen = true;
      this.errorsData = [];
      this.$emit('focus', event);
    },
    blur($event) {
      if ($event && $event?.relatedTarget?.classList.contains('search-field__options__option')) {
        return;
      }

      if (!this.val || (this.removeValueOnType === false && this.search.length > 0)) {
        const searchValue = this.search.trim().toLowerCase();
        if (searchValue === '') {
          this.errorsData = this.isOptional ? [] : [this.validationMessages.emptyValue];
        } else {
          let found = false;
          let foundSimilar = false;
          this.filteredOptions.find(({ value, rawLabel }) => {
            if (value === searchValue || rawLabel.toLowerCase() === searchValue) {
              this.selectOption(value, false);
              found = true;
              return true;
            }

            if (value.toString().includes(searchValue) || rawLabel.toLowerCase().includes(searchValue)) {
              foundSimilar = true;
            }

            return false;
          });

          if (!found) {
            this.errorsData = foundSimilar ? [this.validationMessages.chooseFromList] : [this.validationMessages.emptyList];
          }
        }
      }
      if (this.removeValueOnType === false && !this.search) {
        this.updateSearch(this.modelValue);
      }

      this.isOpen = false;
      this.$emit('blur', this.errorsData);
    },
    calculateSearchScoreForOption(option) {
      const labelLower = option.label.toLowerCase();
      const searchLower = this.search.trim().toLowerCase().replaceAll(/\s+/g, ' ');
      const searchWordsLower = searchLower.split(' ');

      const match = searchWordsLower.some((word) => labelLower.includes(word));
      if (!match) {
        return 0;
      }

      let score = 0;
      labelLower.split(' ').forEach((word, wIndex) => {
        searchWordsLower.forEach((searchWord) => {
          if (searchWord === '') {
            return;
          }
          if (searchWord.length > 2 && word === searchWord) {
            score += wIndex === 0 ? 16 : 12;
          } else if (word.startsWith(searchWord)) {
            score += wIndex === 0 ? 10 : 4;
          } else if (word.includes(searchWord)) {
            score += wIndex === 0 ? 6 : 4;
          }
        });
      });

      for (let i = 0; i < searchLower.length; i += 1) {
        if (labelLower[i] === searchLower[i]) {
          score += 1;
        } else {
          break;
        }
      }

      if (labelLower.startsWith(searchLower)) {
        score += 5;
      }
      if (option.label === option.secondaryLabel) {
        if (labelLower === option.label.toLowerCase()) {
          score += 10;
        } else {
          score += 5;
        }
      }

      return Math.round(score / 5) * 5;
    },
  },
};
</script>

<style lang="scss" scoped>
@use "@sass/design-system/variables" as ds-variables;

.search-field {
  position: relative;

  :global(.fade-enter-active), :global(.fade-leave-active) {
    transition: opacity .2s;
  }

  :global(.fade-enter), :global(.fade-leave-to) {
    opacity: 0;
  }
}

.search-field--errors {
  .search-field__options {
    top: calc(100% - 20px);
  }
}

.search-field__options {
  margin-top: 4px;
  position: absolute;
  width: 100%;
  background: ds-variables.$white;
  z-index: 5;
  padding: 8px 0;
  max-height: calc(8px * 2 + 7.5 * 35px);
  overflow-y: auto;

  &__option {
    padding: 8px 16px;
    cursor: pointer;
    font-size: 14px;

    &:hover {
      background-color: ds-variables.$grey-100;
    }

    &--selected {
      background-color: ds-variables.$grey-100;
    }
  }

  .search-field__options__option--large {
    padding: 12px 16px;
  }
}
</style>
